import type { FormEventHandler } from 'react';
import { memo, useCallback, useState } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { Form } from 'src/shared/components/form/form.js';
import { cnForm } from 'src/shared/components/form/kc-form.constants.js';
import { Input } from 'src/shared/components/input/input.js';
import { PasswordInput } from 'src/shared/components/password-input/password-input.js';
import { cnPasswordStrength } from 'src/shared/components/password-strength-meter/password-strength.constants.js';
import { useFieldMessage } from 'src/shared/hooks/use-field-message.js';
import { useFormValidator } from 'src/shared/hooks/use-form-validator.js';
import { usePasswordStrengthCriteria } from 'src/shared/hooks/use-password-strength-criteria.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';

export interface LoginUpdatePasswordProps {
    kcContext: KcContext.LoginUpdatePassword;
}

export const LoginUpdatePassword = memo<LoginUpdatePasswordProps>(({ kcContext }) => {
    const { url } = kcContext;

    // @ts-ignore - Just believe me. It is custom field
    const isMigration = kcContext.is_migration as boolean;

    // @ts-ignore - Just believe me. It is custom field
    const isFastRegistration = kcContext.is_fast_registration as boolean;

    const { msgStr, advancedMsgStr } = useGetMsg(kcContext);

    const passwordError = useFieldMessage(['password', 'password-new', 'password-confirm'], kcContext);

    let buttonText = msgStr('updatePasswordButton');
    buttonText = isFastRegistration ? msgStr('updatePasswordFastRegistrationButton') : buttonText;

    const setPasswordField = useCallback((formElement: HTMLFormElement) => {
        if (formElement['password-new'] && formElement['password-confirm']) {
            (formElement['password-confirm'] as HTMLInputElement).value = (
                formElement['password-new'] as HTMLInputElement
            ).value;

            return true;
        }

        return false;
    }, []);

    const [formErrors, resetFieldError, onSubmitValidated, submitDisabled] = useFormValidator(
        ['password-new'],
        {
            password: passwordError,
        },
        setPasswordField,
    );

    const [password, setPassword] = useState('');

    const handlePasswordChange = useCallback<FormEventHandler<HTMLInputElement>>((e) => {
        if (!e.target) {
            return;
        }

        setPassword((e.target as HTMLInputElement).value);
    }, []);

    const passwordStrengthLevels = usePasswordStrengthCriteria(msgStr);

    return (
        <Form
            title={msgStr('updatePasswordTitle')}
            action={url.loginAction}
            kcContext={kcContext}
            onSubmit={onSubmitValidated}
        >
            {isMigration && (
                <>
                    <div className={cnForm('message')}>
                        <span>{msgStr('updatePasswordMigrationMessage')}</span>
                    </div>

                    <div className={cnForm('note')}>{msgStr('updatePasswordMigrationNote')}</div>
                </>
            )}
            <PasswordInput
                className={cnPasswordStrength()}
                id="password-new"
                name="password-new"
                errorMessage={advancedMsgStr(formErrors['password-new'] ?? '')}
                hintText={msgStr('passwordStrengthHint')}
                label={msgStr('password')}
                onChange={handlePasswordChange}
                onFocus={resetFieldError}
                password={password}
                strengthLevels={passwordStrengthLevels}
                value={password}
                autoFocus={!passwordError}
            />

            <Input type="submit" value={buttonText} disabled={submitDisabled} />
        </Form>
    );
});
